import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PersonAvatar from '../../../components/PersonAvatar';
import { horarioCorto } from '../../../utils/horario';
import { FamiliarInfo, discountLabel } from '../api';

// FAMILIAR CON AVATAR + TOOLTIP (23-sep-2026, Carlos: «que aparezca el avatar de ese alguien y que se pueda ver
// en un tooltip el estado, activo, clases, ir al perfil»). Hover en escritorio; en celular, un toque lo abre.

const MES_TXT: Record<string, { t: string; cls: string }> = {
  pagado: { t: 'mensualidad pagada', cls: 'text-emerald-600 dark:text-emerald-400' },
  parcial: { t: 'abonó parte de la mensualidad', cls: 'text-amber-600 dark:text-amber-400' },
  debe: { t: 'debe la mensualidad', cls: 'text-red-600 dark:text-red-400' },
  sin_cargo: { t: 'sin mensualidad este mes', cls: 'text-content-3' },
};

export function mesNombre(m?: string) {
  if (!m) return '';
  return new Date(m + '-01T12:00:00').toLocaleDateString('es-MX', { month: 'long' });
}

export function EstadoBadge({ activa }: { activa?: boolean }) {
  return activa === false
    ? <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">baja</span>
    : <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">activa</span>;
}

/** Contenido del tooltip y de la tarjeta de la pestaña Familia: una sola forma de leer a un familiar. */
export function FamiliarDetalle({ f }: { f: FamiliarInfo }) {
  const mes = f.mes_estado ? MES_TXT[f.mes_estado] : null;
  return (
    <div className="space-y-1.5 text-xs text-content-2">
      <p>
        {f.plan ? <b className="text-content">{f.plan}</b> : <span className="text-content-3">Sin plan</span>}
        {f.descuento && (
          <span className="text-brand-blue"> · {f.descuento} ({discountLabel(f.descuento_valor ?? null, f.descuento_pct ? 1 : 0)})</span>
        )}
      </p>
      {f.clases && f.clases.length > 0 ? (
        <ul className="space-y-0.5">
          {f.clases.map((c) => (
            <li key={c.class_id} className="flex items-start gap-1.5">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: c.color || '#94a3b8' }} />
              <span><span className="text-content">{c.name}</span>
                {c.horario.length > 0 && <span className="text-content-3"> · {horarioCorto(c.horario.map((h) => ({ weekday: h.weekday, start_time: h.start })), ' · ')}</span>}
              </span>
            </li>
          ))}
        </ul>
      ) : <p className="text-content-3">Sin clases vigentes</p>}
      {mes && <p className={mes.cls}>{mesNombre(f.mes)}: {mes.t}</p>}
    </div>
  );
}

export default function FamiliarChip({ f, extra }: { f: FamiliarInfo; extra?: React.ReactNode }) {
  const [abierto, setAbierto] = useState(false);
  const t = useRef<number | undefined>(undefined);
  const ref = useRef<HTMLSpanElement>(null);
  const abrir = () => { window.clearTimeout(t.current); t.current = window.setTimeout(() => setAbierto(true), 150); };
  const cerrar = () => { window.clearTimeout(t.current); t.current = window.setTimeout(() => setAbierto(false), 200); };
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);
  useEffect(() => () => window.clearTimeout(t.current), []);

  return (
    <span ref={ref} className="relative inline-flex" onMouseEnter={abrir} onMouseLeave={cerrar} data-testid="familiar-chip">
      {/* El NOMBRE lleva al perfil (Carlos 23-sep: «si quiero dar clic en el nombre no me deja»); el tooltip sale al
          pasar el mouse y, en celular, tocando el AVATAR. */}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface py-0.5 pl-0.5 pr-2 text-xs font-medium text-content hover:border-brand-blue/50">
        <button type="button" onClick={() => setAbierto((v) => !v)} onFocus={abrir} onBlur={cerrar} aria-label={`Ver datos de ${f.nombre}`} data-testid="familiar-avatar" className="rounded-full">
          <PersonAvatar contactId={f.contact_id} hasPhoto={f.has_photo} name={f.first_name || f.nombre} lastName={f.last_name} size="h-6 w-6" initialClass="text-[10px]" />
        </button>
        <Link to={`/alumnos/${f.contact_id}`} data-testid="familiar-nombre"
          className={'hover:text-brand-blue hover:underline ' + (f.activa === false ? 'text-content-3 line-through' : '')}>{f.nombre}</Link>
        {f.activa === false && <EstadoBadge activa={false} />}
        {extra}
      </span>
      {abierto && (
        <div role="tooltip" data-testid="familiar-tooltip" onMouseEnter={abrir} onMouseLeave={cerrar}
          className="absolute left-0 top-full z-30 mt-1.5 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface p-3 text-left shadow-xl">
          <div className="mb-2 flex items-center gap-2.5">
            <PersonAvatar contactId={f.contact_id} hasPhoto={f.has_photo} name={f.first_name || f.nombre} lastName={f.last_name} size="h-10 w-10" initialClass="text-sm" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-content">{f.nombre}</p>
              <EstadoBadge activa={f.activa} />
            </div>
          </div>
          <FamiliarDetalle f={f} />
          <Link to={`/alumnos/${f.contact_id}`} onClick={() => setAbierto(false)}
            className="mt-2 inline-block text-xs font-medium text-brand-blue hover:underline">Ir al perfil →</Link>
        </div>
      )}
    </span>
  );
}
